"use client";

import Icon from "@/components/icons/Icons";
import { motion } from "framer-motion";
import { useRef } from "react";

type Step = {
  id: number;
  label: string;
};

type StepProgressProps = {
  steps: Step[];
  currentStep: number;
  onStepChange?: (step: number) => void;
  allowBackwardClick?: boolean;
  allowForwardClick?: boolean;
  className?: string;
};

export default function StepProgress({
  steps,
  currentStep,
  onStepChange,
  allowBackwardClick = true,
  allowForwardClick = false,
  className,
}: StepProgressProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const handleStepClick = (stepId: number) => {
    if (stepId === currentStep) return;

    const isForward = stepId > currentStep;
    const isBackward = stepId < currentStep;

    if (isForward && !allowForwardClick) return;

    if (isBackward && !allowBackwardClick) return;

    onStepChange?.(stepId);
  };

  return (
    <div
      ref={containerRef}
      className={`space-y-2 w-full max-w-md ${className}`}
    >
      {steps.map((step) => {
        const isActive = currentStep === step.id;
        const isCompleted = currentStep > step.id;

        return (
          <div
            key={step.id}
            className="relative cursor-pointer"
            onClick={() => handleStepClick(step.id)}
          >
            {/* Active background highlight */}
            {isActive && (
              <motion.div
                layoutId="activeStep"
                className="absolute inset-0 rounded-xl bg-[#4B4B4B] border border-[#FFFFFF]"
                transition={{ type: "spring", stiffness: 400, damping: 30 }}
              />
            )}

            <div className="relative flex items-center gap-3 p-4 rounded-xl">
              {/* Left icon */}
              {isCompleted ? (
                <div className="h-9 w-9 shrink-0 rounded-full bg-secondaryGreen flex items-center justify-center">
                  <Icon.rightSignIcon className="text-white" />
                </div>
              ) : (
                <div
                  className={`h-9 w-9 rounded-full flex items-center justify-center border
                    ${isActive ? "border-white bg-white text-primaryBlack" : "border-gray-500 text-gray-400"}
                  `}
                >
                  {step.id}
                </div>
              )}

              {/* Label */}
              <span
                className={`text-sm ${isActive ? "text-white font-medium" : "text-gray-300"}`}
              >
                {step.label}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
